import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Box, Paper, Typography, IconButton, Chip } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { FIELD_TYPE_MAP } from "../formsUtils";

/** Card de um campo no canvas: arrastável (handle), selecionável, duplicável. */
export default function SortableField({ field, selected, onSelect, onDelete, onDuplicate }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.id });
  const meta = FIELD_TYPE_MAP[field.type] || {};

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const isLayout = ["section", "paragraph", "cover", "consent"].includes(field.type);

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      elevation={selected ? 3 : 0}
      onClick={() => onSelect(field.id)}
      sx={{
        display: "flex", alignItems: "flex-start", gap: 1, p: 1.5, mb: 1,
        border: isDragging ? "1px dashed #3f51b5" : selected ? "2px solid #3f51b5" : "1px solid #e0e0e0",
        borderRadius: 1, cursor: "pointer",
        background: isDragging ? "#eef1ff" : isLayout ? "#fafafa" : "#fff",
        transition: "border-color .15s, background-color .15s",
      }}
    >
      <Box {...attributes} {...listeners} sx={{ cursor: "grab", color: "#999", pt: 0.3 }} onClick={(e) => e.stopPropagation()}>
        <DragIndicatorIcon fontSize="small" />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <i className={meta.icon} style={{ color: "#3f51b5", fontSize: 13 }} />
          <Typography variant="body1" sx={{ fontWeight: 500 }} noWrap>
            {field.label || "(sem rótulo)"}
          </Typography>
          {field.required && <Chip size="small" label="obrigatório" color="warning" variant="outlined" />}
          {field.visibleIf && <Chip size="small" label="condicional" color="info" variant="outlined" />}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {meta.label}{field.name ? ` · ${field.name}` : ""}
        </Typography>
        {Array.isArray(field.options) && (
          <Box sx={{ mt: 0.5 }}>
            {field.options.slice(0, 4).map((o) => (
              <Chip key={o.value} size="small" label={o.label} sx={{ mr: 0.5, mb: 0.5 }} variant="outlined" />
            ))}
            {field.options.length > 4 && <Chip size="small" label={`+${field.options.length - 4}`} />}
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDuplicate(field.id); }}>
          <ContentCopyIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); onDelete(field.id); }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );
}
